import { Alert, Box, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/Warning';
import { differenceInDays, format, isPast, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { dateFnsLocale } from '@/utils/locale';

export type InspectionStatusKind = 'overdue' | 'expiring' | 'valid';

export interface InspectionStatus {
  kind: InspectionStatusKind;
  daysUntil: number;
  dueDate: Date;
}

/**
 * Shared inspection-due rules used by the full vehicle card and the
 * work-order create summary. Same thresholds as VehicleDetailModal:
 * past due → overdue; 30 days or fewer remaining → expiring; otherwise valid.
 */
export function getInspectionStatusKind(
  nextInspectionDue: string | null | undefined,
  now: Date = new Date(),
): InspectionStatus | null {
  if (!nextInspectionDue) return null;
  const dueDate = new Date(nextInspectionDue);
  if (!isValid(dueDate)) return null;
  const daysUntil = differenceInDays(dueDate, now);
  if (isPast(dueDate)) {
    return { kind: 'overdue', daysUntil, dueDate };
  }
  if (daysUntil <= 30) {
    return { kind: 'expiring', daysUntil, dueDate };
  }
  return { kind: 'valid', daysUntil, dueDate };
}

interface VehicleInspectionSummaryProps {
  traficomData?: { nextInspectionDue?: string | null } | null;
}

export function VehicleInspectionSummary({ traficomData }: VehicleInspectionSummaryProps) {
  const { t } = useTranslation('vehicles');
  const status = getInspectionStatusKind(traficomData?.nextInspectionDue);
  if (!status) return null;

  const severity =
    status.kind === 'overdue' ? 'error' : status.kind === 'expiring' ? 'warning' : 'success';
  const icon =
    status.kind === 'overdue' ? <ErrorIcon /> : status.kind === 'expiring' ? <WarningIcon /> : <VerifiedIcon />;
  const label =
    status.kind === 'overdue'
      ? t('traficom.inspectionOverdue')
      : status.kind === 'expiring'
        ? `${String(status.daysUntil)} ${t('traficom.daysLeft')}`
        : t('traficom.inspectionValid');

  return (
    <Alert severity={severity} icon={icon} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="body2">
          {t('traficom.nextInspection')}: {format(status.dueDate, 'd.M.yyyy', { locale: dateFnsLocale() })}
        </Typography>
      </Box>
    </Alert>
  );
}
